import { FC, useState } from 'react';

import { BsChatSquareDotsFill } from 'react-icons/bs';
import { uniqBy } from 'lodash';
import cn from 'classnames';
import { toastError } from 'ui/partials/Toast';
import { useCreateNote, useGetNotes } from 'modules/account/contact/hooks';
import { Comment } from 'ui/components/common';
import { convertToMegabytes, useUploadToUploadCare } from 'utils/helpers';
import NoteCommentForm from 'ui/components/common/Entry/NotesEntry/NoteCommentForm';
import AttachmentsTooLargeModal from 'modules/account/contact/components/AttachmentsTooLargeModal';
import { useGetSubscription } from 'modules/account/settings/hooks';
import useModal from 'ui/partials/Modal/useModal';
import { type FileRejection } from 'react-dropzone';
import { UploadcareGroup } from '@uploadcare/upload-client';
import { FaArrowDownShortWide, FaArrowUpWideShort } from 'react-icons/fa6';

const maxSize = 1024 * 1024 * 5;

type PROPS = {
  taskId: string;
};

const TaskComments: FC<PROPS> = ({ taskId }) => {
  const { mutate: addComment } = useCreateNote();
  const { showModal } = useModal();
  const { data: subscription } = useGetSubscription();
  const { mutateAsync: uploadFiles, isLoading: uploadLoading } =
    useUploadToUploadCare();

  const [orderBy, setOrderBy] = useState<'ASC' | 'DESC'>('ASC');

  const { data: comments } = useGetNotes({
    referenceId: taskId,
    include: ['comments', 'attachments', 'accountUser'],
    orderBy,
  });

  const [commentAttachments, setCommentAttachments] = useState<any>([]);
  const [showAddComment, setShowAddComment] = useState(false);
  const [addCommentValue, setAddCommentValue] = useState('');

  const showAttachmentsTooLargeModal = (fileSize: string) => {
    showModal({
      title: 'Attachments',
      disableOutsideClick: true,
      content: <AttachmentsTooLargeModal fileSize={fileSize} maxSize={5} />,
    });
  };

  const commentsOnDrop = (_acceptedFiles: File[]) => {
    setCommentAttachments((prevVal: any) =>
      uniqBy([...prevVal, ..._acceptedFiles], 'name'),
    );
    const sizeUploaded = commentAttachments.reduce(
      (acc: any, curr: any) => acc + curr.fileSize,
      0,
    );
    const sizeToUpload = [...commentAttachments, ..._acceptedFiles].reduce(
      (acc: any, curr: any) => acc + curr.size,
      0,
    );
    if (sizeUploaded + sizeToUpload > maxSize) {
      toastError(convertToMegabytes(sizeUploaded + sizeToUpload));
    }
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    const _totalSize = fileRejections.reduce((acc, { errors, file }) => {
      if (errors[0].code === 'file-too-large') {
        return acc + file.size;
      }
      return acc;
    }, 0);
    if (fileRejections[0].errors[0].code === 'file-too-large') {
      showAttachmentsTooLargeModal(convertToMegabytes(_totalSize));
    }
  };

  const removeAttachmentFromCommentForm = (name: string) => {
    setCommentAttachments((prevVal: any) =>
      prevVal.filter((val: any) => !(val.name === name)),
    );
  };

  const handleSaveComment = async () => {
    const uploadedAttachments =
      commentAttachments.length &&
      (await uploadFiles(commentAttachments, {
        onSuccess: () => setCommentAttachments([]),
      }));

    addComment(
      {
        content: addCommentValue,
        referenceId: taskId,
        source: 'Task',
        ...(uploadedAttachments && {
          attachments: uploadedAttachments.files.map(
            (file: UploadcareGroup['files'][0]) => ({
              fileUrl: file.cdnUrl as string,
              fileType: file.mimeType as string,
              fileName: file.originalFilename as string,
              fileSize: file.size as number,
              source: 'Task',
              ...(file.isImage && { fileThumbnail: file.cdnUrl as string }),
            }),
          ),
        }),
      },
      {
        onSuccess: () => {
          setAddCommentValue('');
          setShowAddComment(false);
        },
      },
    );
  };

  const handleCancelComment = () => {
    setAddCommentValue('');
    setCommentAttachments([]);
    setShowAddComment(false);
  };

  const totalAddCommentAttachmentSize = commentAttachments?.reduce(
    (acc: number, curr: any) => acc + curr.size,
    0,
  );

  return (
    <>
      <div className="flex justify-between pt-4">
        {!subscription?.isExpired && (
          <button
            onClick={() => setShowAddComment((prev) => !prev)}
            type="button"
            className={cn(
              'w-full  disabled:bg-transparent',
              'flex hover:text-primary',
              'text-xs leading-4',
              'items-center',
              'font-semibold',
              showAddComment &&
                'cursor-not-allowed text-disabled hover:text-disabled',
            )}
            disabled={showAddComment}
          >
            <BsChatSquareDotsFill
              className={cn(
                'mr-[10px] text-primary',
                showAddComment && 'text-disabled',
              )}
            />
            <span>ADD COMMENT</span>
          </button>
        )}
        <div
          className={cn(
            'flex items-center gap-2',
            showAddComment && 'text-disabled',
          )}
        >
          <div className="text-xs leading-4  font-semibold">SORT: </div>
          {orderBy === 'DESC' && (
            <FaArrowUpWideShort
              role="button"
              onClick={() => setOrderBy('ASC')}
              className="hover:text-primary-base transition w-4 h-4 "
            />
          )}
          {orderBy === 'ASC' && (
            <FaArrowDownShortWide
              role="button"
              onClick={() => setOrderBy('DESC')}
              className="hover:text-primary-base transition w-4 h-4"
            />
          )}
        </div>
      </div>
      {showAddComment && (
        <NoteCommentForm
          name="add-comment"
          placeholder="Add Comment"
          onChange={setAddCommentValue}
          attachments={commentAttachments}
          onRemoveAttachment={removeAttachmentFromCommentForm}
          disabled={!addCommentValue || totalAddCommentAttachmentSize > maxSize}
          onCancel={handleCancelComment}
          onSave={handleSaveComment}
          onDrop={commentsOnDrop}
          onDropRejected={onDropRejected}
          isLoading={uploadLoading}
        />
      )}
      <div className="space-y-7">
        {comments?.map((comment) => (
          <Comment
            key={comment._id}
            comment={comment}
            user={comment?.userId}
            maxSize={maxSize}
            referenceId={taskId}
          />
        ))}
      </div>
    </>
  );
};

export default TaskComments;
